/**
 * KeyIssuesAccordion Component
 *
 * Task 174: Configuration Form - Key Issues Accordion
 *
 * Section D of the Drafting configuration form containing:
 * - Checkboxes for: Payment, Termination, Liability, IP, Confidentiality, Data Protection, Warranties, Indemnification
 * - When checked, expand inline section with follow-up questions
 * - Follow-up questions as form fields (dropdowns, text inputs as appropriate)
 * - Smooth accordion animation
 * - Store responses in key_issues JSONB structure
 *
 * Features:
 * - Expandable accordion sections for each key issue
 * - Form state management with useState
 * - onChange callback for parent integration
 * - initialValues prop for pre-populating form
 * - Smooth CSS animations
 */

import React, { useState, useEffect } from 'react';
import { KeyIssues, KeyIssueSelection } from '../../types/drafting';
import './KeyIssuesAccordion.css';

// ==============================================================================
// Type Definitions
// ==============================================================================

export interface KeyIssuesAccordionProps {
  /** Callback when any key issue or follow-up question changes */
  onChange?: (keyIssues: KeyIssues) => void;
  /** Initial values to pre-populate the form */
  initialValues?: KeyIssues;
  /**
   * Plan 253: limit the rendered issues to these keys, in KEY_ISSUE_TYPES
   * order. A SoW shows only the issues its clauses use.
   */
  issueKeys?: ReadonlyArray<string>;
}

// ==============================================================================
// Constants
// ==============================================================================

/** Key issue types available for selection */
const KEY_ISSUE_TYPES = [
  { key: 'payment', label: 'Payment' },
  { key: 'termination', label: 'Termination' },
  { key: 'liability', label: 'Liability' },
  { key: 'ip', label: 'IP' },
  { key: 'confidentiality', label: 'Confidentiality' },
  { key: 'data_protection', label: 'Data Protection' },
  { key: 'warranties', label: 'Warranties' },
  { key: 'indemnification', label: 'Indemnification' },
] as const;

/** Payment terms options */
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Select payment terms...' },
  { value: 'net_7', label: 'Net 7 days' },
  { value: 'net_14', label: 'Net 14 days' },
  { value: 'net_30', label: 'Net 30 days' },
  { value: 'net_45', label: 'Net 45 days' },
  { value: 'net_60', label: 'Net 60 days' },
  { value: 'on_receipt', label: 'Due on receipt' },
];

/** Notice period options */
const NOTICE_PERIOD_OPTIONS = [
  { value: '', label: 'Select notice period...' },
  { value: '7_days', label: '7 days' },
  { value: '14_days', label: '14 days' },
  { value: '30_days', label: '30 days' },
  { value: '60_days', label: '60 days' },
  { value: '90_days', label: '90 days' },
];

/** Liability cap options */
const LIABILITY_CAP_OPTIONS = [
  { value: '', label: 'Select liability cap...' },
  { value: 'contract_value', label: 'Cap at contract value' },
  { value: 'annual_fees', label: 'Cap at annual fees' },
  { value: '12_months_fees', label: 'Cap at 12 months fees' },
  { value: 'fixed_amount', label: 'Fixed amount' },
  { value: 'unlimited', label: 'Unlimited (not recommended)' },
];

/** IP ownership options */
const IP_OWNERSHIP_OPTIONS = [
  { value: '', label: 'Select IP ownership...' },
  { value: 'customer_owns', label: 'Customer owns all deliverables' },
  { value: 'vendor_owns', label: 'Vendor retains ownership' },
  { value: 'joint_ownership', label: 'Joint ownership' },
  { value: 'license_grant', label: 'Licence grant to customer' },
];

// ==============================================================================
// Default State
// ==============================================================================

const DEFAULT_KEY_ISSUE: KeyIssueSelection = {
  selected: false,
};

const DEFAULT_KEY_ISSUES: KeyIssues = {
  payment: { ...DEFAULT_KEY_ISSUE },
  termination: { ...DEFAULT_KEY_ISSUE },
  liability: { ...DEFAULT_KEY_ISSUE },
  ip: { ...DEFAULT_KEY_ISSUE },
  confidentiality: { ...DEFAULT_KEY_ISSUE },
  data_protection: { ...DEFAULT_KEY_ISSUE },
  warranties: { ...DEFAULT_KEY_ISSUE },
  indemnification: { ...DEFAULT_KEY_ISSUE },
};

// ==============================================================================
// Component
// ==============================================================================

const KeyIssuesAccordion: React.FC<KeyIssuesAccordionProps> = ({
  onChange,
  initialValues,
  issueKeys,
}) => {
  // Merge initial values with defaults
  const [keyIssues, setKeyIssues] = useState<KeyIssues>(() => {
    const merged = { ...DEFAULT_KEY_ISSUES };
    if (initialValues) {
      Object.keys(initialValues).forEach((key) => {
        const issueKey = key as keyof KeyIssues;
        if (initialValues[issueKey]) {
          merged[issueKey] = {
            ...DEFAULT_KEY_ISSUE,
            ...initialValues[issueKey],
          };
        }
      });
    }
    return merged;
  });

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      onChange(keyIssues);
    }
  }, [keyIssues, onChange]);

  // Toggle key issue selection
  const toggleKeyIssue = (key: keyof KeyIssues) => {
    setKeyIssues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        selected: !prev[key]?.selected,
      },
    }));
  };

  // Update a follow-up field for a key issue
  const updateFollowUp = <K extends keyof KeyIssues>(
    issueKey: K,
    field: string,
    value: boolean | string | number
  ) => {
    setKeyIssues((prev) => ({
      ...prev,
      [issueKey]: {
        ...prev[issueKey],
        [field]: value,
      },
    }));
  };

  // Check if a key issue is selected
  const isSelected = (key: keyof KeyIssues): boolean => {
    return keyIssues[key]?.selected || false;
  };

  // Get follow-up value
  const getFollowUpValue = <T,>(key: keyof KeyIssues, field: string, defaultValue: T): T => {
    const issue = keyIssues[key];
    if (issue && field in issue) {
      return (issue as Record<string, unknown>)[field] as T;
    }
    return defaultValue;
  };

  // Render follow-up questions for Payment
  const renderPaymentFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="payment-follow-up"
      role="region"
      aria-label="Payment follow-up questions"
    >
      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('payment', 'late_payment_interest', false)}
            onChange={(e) => updateFollowUp('payment', 'late_payment_interest', e.target.checked)}
          />
          <span className="checkbox-text">Late payment interest</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label htmlFor="payment_terms" className="field-label">
          Payment Terms
        </label>
        <select
          id="payment_terms"
          className="form-select"
          value={getFollowUpValue('payment', 'payment_terms', '')}
          onChange={(e) => updateFollowUp('payment', 'payment_terms', e.target.value)}
        >
          {PAYMENT_TERMS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('payment', 'suspension_for_nonpayment', false)}
            onChange={(e) => updateFollowUp('payment', 'suspension_for_nonpayment', e.target.checked)}
          />
          <span className="checkbox-text">Right to suspend for non-payment</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up questions for Termination
  const renderTerminationFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="termination-follow-up"
      role="region"
      aria-label="Termination follow-up questions"
    >
      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('termination', 'termination_for_convenience', false)}
            onChange={(e) => updateFollowUp('termination', 'termination_for_convenience', e.target.checked)}
          />
          <span className="checkbox-text">Termination for convenience</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label htmlFor="notice_period" className="field-label">
          Notice Period
        </label>
        <select
          id="notice_period"
          className="form-select"
          value={getFollowUpValue('termination', 'notice_period', '')}
          onChange={(e) => updateFollowUp('termination', 'notice_period', e.target.value)}
        >
          {NOTICE_PERIOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="follow-up-field">
        <label htmlFor="cure_period" className="field-label">
          Cure Period (days)
        </label>
        <input
          type="number"
          id="cure_period"
          className="form-input"
          min={1}
          value={getFollowUpValue('termination', 'cure_period', '')}
          onChange={(e) => updateFollowUp('termination', 'cure_period', e.target.value ? parseInt(e.target.value) : '')}
          placeholder="e.g., 30"
        />
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('termination', 'insolvency_termination', false)}
            onChange={(e) => updateFollowUp('termination', 'insolvency_termination', e.target.checked)}
          />
          <span className="checkbox-text">Termination for insolvency</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up questions for Liability
  const renderLiabilityFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="liability-follow-up"
      role="region"
      aria-label="Liability follow-up questions"
    >
      <div className="follow-up-field">
        <label htmlFor="liability_cap" className="field-label">
          Liability Cap
        </label>
        <select
          id="liability_cap"
          className="form-select"
          value={getFollowUpValue('liability', 'liability_cap', '')}
          onChange={(e) => updateFollowUp('liability', 'liability_cap', e.target.value)}
        >
          {LIABILITY_CAP_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('liability', 'exclude_consequential', false)}
            onChange={(e) => updateFollowUp('liability', 'exclude_consequential', e.target.checked)}
          />
          <span className="checkbox-text">Exclude consequential damages</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('liability', 'mutual_limitation', false)}
            onChange={(e) => updateFollowUp('liability', 'mutual_limitation', e.target.checked)}
          />
          <span className="checkbox-text">Mutual limitation</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up questions for IP
  const renderIPFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="ip-follow-up"
      role="region"
      aria-label="IP follow-up questions"
    >
      <div className="follow-up-field">
        <label htmlFor="ip_ownership" className="field-label">
          IP Ownership
        </label>
        <select
          id="ip_ownership"
          className="form-select"
          value={getFollowUpValue('ip', 'ip_ownership', '')}
          onChange={(e) => updateFollowUp('ip', 'ip_ownership', e.target.value)}
        >
          {IP_OWNERSHIP_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('ip', 'background_ip', false)}
            onChange={(e) => updateFollowUp('ip', 'background_ip', e.target.checked)}
          />
          <span className="checkbox-text">Vendor retains background IP</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('ip', 'license_back', false)}
            onChange={(e) => updateFollowUp('ip', 'license_back', e.target.checked)}
          />
          <span className="checkbox-text">Licence-back for improvements</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('ip', 'ip_indemnification', false)}
            onChange={(e) => updateFollowUp('ip', 'ip_indemnification', e.target.checked)}
          />
          <span className="checkbox-text">IP indemnification</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up questions for Confidentiality
  const renderConfidentialityFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="confidentiality-follow-up"
      role="region"
      aria-label="Confidentiality follow-up questions"
    >
      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('confidentiality', 'mutual_obligations', false)}
            onChange={(e) => updateFollowUp('confidentiality', 'mutual_obligations', e.target.checked)}
          />
          <span className="checkbox-text">Mutual confidentiality obligations</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label htmlFor="confidentiality_period" className="field-label">
          Confidentiality Period (years)
        </label>
        <input
          type="number"
          id="confidentiality_period"
          className="form-input"
          min={1}
          value={getFollowUpValue('confidentiality', 'confidentiality_period', '')}
          onChange={(e) => updateFollowUp('confidentiality', 'confidentiality_period', e.target.value ? parseInt(e.target.value) : '')}
          placeholder="e.g., 3"
        />
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('confidentiality', 'return_of_information', false)}
            onChange={(e) => updateFollowUp('confidentiality', 'return_of_information', e.target.checked)}
          />
          <span className="checkbox-text">Return of information on termination</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up questions for Data Protection
  const renderDataProtectionFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="data_protection-follow-up"
      role="region"
      aria-label="Data Protection follow-up questions"
    >
      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('data_protection', 'gdpr_compliance', false)}
            onChange={(e) => updateFollowUp('data_protection', 'gdpr_compliance', e.target.checked)}
          />
          <span className="checkbox-text">GDPR compliance requirements</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('data_protection', 'privacy_act_compliance', false)}
            onChange={(e) => updateFollowUp('data_protection', 'privacy_act_compliance', e.target.checked)}
          />
          <span className="checkbox-text">Australian Privacy Act compliance</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('data_protection', 'breach_notification', false)}
            onChange={(e) => updateFollowUp('data_protection', 'breach_notification', e.target.checked)}
          />
          <span className="checkbox-text">Data breach notification requirements</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('data_protection', 'sub_processors', false)}
            onChange={(e) => updateFollowUp('data_protection', 'sub_processors', e.target.checked)}
          />
          <span className="checkbox-text">Sub-processor approval requirements</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up questions for Warranties
  const renderWarrantiesFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="warranties-follow-up"
      role="region"
      aria-label="Warranties follow-up questions"
    >
      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('warranties', 'fitness_for_purpose', false)}
            onChange={(e) => updateFollowUp('warranties', 'fitness_for_purpose', e.target.checked)}
          />
          <span className="checkbox-text">Fitness for purpose warranty</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('warranties', 'non_infringement', false)}
            onChange={(e) => updateFollowUp('warranties', 'non_infringement', e.target.checked)}
          />
          <span className="checkbox-text">Non-infringement warranty</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label htmlFor="warranty_period" className="field-label">
          Warranty Period (months)
        </label>
        <input
          type="number"
          id="warranty_period"
          className="form-input"
          min={1}
          value={getFollowUpValue('warranties', 'warranty_period', '')}
          onChange={(e) => updateFollowUp('warranties', 'warranty_period', e.target.value ? parseInt(e.target.value) : '')}
          placeholder="e.g., 12"
        />
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('warranties', 'warranty_disclaimer', false)}
            onChange={(e) => updateFollowUp('warranties', 'warranty_disclaimer', e.target.checked)}
          />
          <span className="checkbox-text">Include warranty disclaimers</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up questions for Indemnification
  const renderIndemnificationFollowUp = () => (
    <div
      className="key-issue-follow-up expanded"
      data-testid="indemnification-follow-up"
      role="region"
      aria-label="Indemnification follow-up questions"
    >
      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('indemnification', 'mutual_indemnification', false)}
            onChange={(e) => updateFollowUp('indemnification', 'mutual_indemnification', e.target.checked)}
          />
          <span className="checkbox-text">Mutual indemnification</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('indemnification', 'ip_indemnity', false)}
            onChange={(e) => updateFollowUp('indemnification', 'ip_indemnity', e.target.checked)}
          />
          <span className="checkbox-text">IP infringement indemnity</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('indemnification', 'third_party_claims', false)}
            onChange={(e) => updateFollowUp('indemnification', 'third_party_claims', e.target.checked)}
          />
          <span className="checkbox-text">Third party claims coverage</span>
        </label>
      </div>

      <div className="follow-up-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={getFollowUpValue('indemnification', 'indemnity_cap', false)}
            onChange={(e) => updateFollowUp('indemnification', 'indemnity_cap', e.target.checked)}
          />
          <span className="checkbox-text">Cap on indemnity obligations</span>
        </label>
      </div>
    </div>
  );

  // Render follow-up content based on key issue type
  const renderFollowUp = (key: keyof KeyIssues) => {
    if (!isSelected(key)) return null;

    switch (key) {
      case 'payment':
        return renderPaymentFollowUp();
      case 'termination':
        return renderTerminationFollowUp();
      case 'liability':
        return renderLiabilityFollowUp();
      case 'ip':
        return renderIPFollowUp();
      case 'confidentiality':
        return renderConfidentialityFollowUp();
      case 'data_protection':
        return renderDataProtectionFollowUp();
      case 'warranties':
        return renderWarrantiesFollowUp();
      case 'indemnification':
        return renderIndemnificationFollowUp();
      default:
        return null;
    }
  };

  return (
    <section
      className="key-issues-section"
      data-testid="key-issues-section"
    >
      <h2>Key Issues</h2>
      <p className="section-helper">
        Select topics to customise the clauses in your contract. Follow-up questions will appear for each selected topic.
      </p>

      <div className="key-issues-list">
        {KEY_ISSUE_TYPES.filter((issue) => !issueKeys || issueKeys.includes(issue.key)).map((issue) => (
          <div key={issue.key} className="key-issue-item">
            <label className="key-issue-checkbox">
              <input
                type="checkbox"
                id={`key-issue-${issue.key}`}
                checked={isSelected(issue.key as keyof KeyIssues)}
                onChange={() => toggleKeyIssue(issue.key as keyof KeyIssues)}
              />
              <span className="checkbox-text">{issue.label}</span>
            </label>
            {renderFollowUp(issue.key as keyof KeyIssues)}
          </div>
        ))}
      </div>
    </section>
  );
};

export default KeyIssuesAccordion;
